import { LoginContextProvider } from "@/flokit/utils/login";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { ROUTER_VARIABLES } from "../utils/router-variables";
import { useAmILoggedIn } from "./api-docs";

// Urls that will not be authenticated
// Used for non admin urls

const WorkAllocationUrls = [
  new RegExp(
    "^/v2/app/[a-zA-Z0-9-]+/work-allocation/programs/[a-zA-Z0-9-]+/request-matter",
  ),
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/work-allocation/associate/.*"), // work allocation associate stuff,
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/work-allocation/programs/[0-9]+/.*"), // work allocation feedback portal
];

const MemberUrl = new RegExp("^/v2/app/[a-zA-Z0-9-]+/member"); // any member page

const urls = [
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/login"), // login page
  new RegExp("^/v2/app/playground"), // playground
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/registration/.*"), // Employer/Student registration pages serendipitous
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/events/serendipitous/[0-9]+/student/.*"), // Student video chat experience serendipitous
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/events/serendipitous/[0-9]+/employer/.*"), // Employer video chat experience serendipitous
  new RegExp("^/v2/linkExpired"), // Link expired
  new RegExp("^/v2/job-sign-in-expired"),
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/jobs/.*"), // careers page for ats
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/jobs"), // careers page for ats
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/candidate-review"), // candidate-review
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/candidate-review/.*"), // candidate-review
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/[a-zA-Z0-9-]+/jobs"),
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/events/preselect/[0-9]+/student/.*"), // Preselect student pages
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/events/preselect/[0-9]+/interviewer/.*"), // Preselect interviewer pages
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/events/lottery/[0-9]+/interviewer/.*"), // Lottery interviewer pages
  new RegExp("^/v2/app/[a-zA-Z0-9-]+/events/lottery/[0-9]+/student/.*"), // Preselect student pages
  new RegExp("^/v2/app/aspire"), // aspire
  new RegExp("^/v2/app/forward"), // forward
  ...WorkAllocationUrls,
  MemberUrl,
];

export const useAdminAuthNeeded = () => {
  if (process.browser) {
    let tempAuthNeeded = true;
    for (let i = 0; i < urls.length; i++) {
      if (window.location.pathname.match(urls[i])) {
        tempAuthNeeded = false;
        break;
      }
    }
    return tempAuthNeeded;
  }
  return false;
};

// Component redirects whenever user is not logged in
const AdminLoginGuard = (props: { children: React.ReactNode }) => {
  const router = useRouter();
  const authNeeded = useAdminAuthNeeded();
  const res = useAmILoggedIn({ query: { retry: 2, enabled: authNeeded } });

  const { org } = router.query;

  useEffect(() => {
    if (process.browser) {
      if (authNeeded) {
        if (!res.isLoading) {
          if (!res.data?.success || res.isError) {
            if (window.location.pathname !== "/") {
              if (org == null) {
                window.location.replace("/");
              } else {
                // If org is in path then redirect to org specific page
                window.location.replace("/app/" + org + "/admin/");
              }
            }
          } else {
            // If org from cookie does not match org in path redirect
            if (org != null && org != res.data.orgFriendlyName) {
              window.location.replace("/app/" + org + "/admin/");
            }
          }
        }
      } else if (
        res.data?.success &&
        MemberUrl.test(window.location.pathname)
      ) {
        // If an admin is logged in and the path is a member path, redirect to the admin path
        window.location.replace(
          window.location.pathname.replace("member", "admin"),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res.data, res.isError, org, authNeeded]);

  if (!process.browser) {
    return null;
  }

  if (!authNeeded) {
    return <>{props.children}</>;
  }

  return res?.data?.success || window.location.host.includes("localhost") ? (
    <LoginContextProvider email={res?.data?.email} userId={res?.data?.uid}>
      {props.children}
    </LoginContextProvider>
  ) : null;
};

export function SuperAdminLoginGuard(props: { children: React.ReactNode }) {
  const router = useRouter();
  const authNeeded = useAdminAuthNeeded();
  const { [ROUTER_VARIABLES.org]: orgFriendlyNameFromRoute } = router.query;

  const { data, isLoading } = useAmILoggedIn({
    query: { retry: 2, enabled: authNeeded },
  });
  const orgFriendlyNameFromCookie = data?.orgFriendlyName;

  if (!isLoading) {
    if (!orgFriendlyNameFromRoute || !orgFriendlyNameFromCookie) {
      window.location.replace("/");
      return null;
    } else if (orgFriendlyNameFromCookie !== orgFriendlyNameFromRoute) {
      window.location.replace("/");
      return null;
    } else if (orgFriendlyNameFromCookie !== "superadmin") {
      window.location.replace("/");
      return null;
    }
  }

  return data?.success && !isLoading ? (
    <AdminLoginGuard>{props.children}</AdminLoginGuard>
  ) : null;
}

export default AdminLoginGuard;
